// Tab-level "Alice is done" signal, so a reply can be monitored from another
// tab or app. While a reply is pending the tab title and favicon show a busy
// state; if it lands while the user isn't looking (tab hidden or window
// unfocused), the tab keeps a "ready" badge until they come back, and an
// opt-in desktop notification fires.
//
// Usage: beginReply(key) when a request goes out, endReply(key, notice) when it
// settles. Pass notice = null for a silent end (cancelled, unmounted).
import { reactive } from "vue";

const PREF_KEY = "alice.replyNotifications";
// Red = "come look at this tab"; the title tells ready (✅) from failed (⚠️).
const BADGE_COLORS = {
  busy: "#e8943a", // --vp-oranje-100
  ready: "#f05668", // --vp-rood-100
  failed: "#f05668", // --vp-rood-100
};

const supported = typeof window !== "undefined" && "Notification" in window;

// Reactive so the chat header's bell button reflects permission + preference.
export const replyAttention = reactive({
  supported,
  permission: supported ? Notification.permission : "unsupported",
  wanted: readPref(),
});

const pending = new Set();
const openNotifications = new Set();
// Replies that settled while the user wasn't looking.
const unseen = { ready: 0, failed: 0 };
let baseTitle = null;
let baseIcon = null;
let iconImage = null;
let renderedState = "idle";

function readPref() {
  try {
    return localStorage.getItem(PREF_KEY) !== "off";
  } catch (e) {
    return true;
  }
}

function writePref(on) {
  try {
    localStorage.setItem(PREF_KEY, on ? "on" : "off");
  } catch (e) {
    // Private window / blocked storage: the toggle just won't persist.
  }
}

export function notificationsEnabled() {
  return replyAttention.wanted && replyAttention.permission === "granted";
}

// Must run from a user gesture (click) — browsers ignore it otherwise.
export async function enableNotifications() {
  if (!supported) return false;
  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch (e) {
      permission = Notification.permission;
    }
  }
  replyAttention.permission = permission;
  if (permission !== "granted") return false;
  replyAttention.wanted = true;
  writePref(true);
  // Immediate confirmation doubles as a setup check: if nothing pops up, the
  // OS is blocking the browser's notifications.
  showNotification({
    title: "Notifications are on",
    body: "Alice will pop up here when an answer is ready.",
    sticky: false,
  });
  return true;
}

export function disableNotifications() {
  replyAttention.wanted = false;
  writePref(false);
}

function userIsLooking() {
  return document.visibilityState === "visible" && document.hasFocus();
}

export function beginReply(key) {
  pending.add(key);
  render();
}

// notice: { title, body, failed, onClick } or null for a silent end.
export function endReply(key, notice) {
  if (!pending.delete(key)) return;
  if (notice && !userIsLooking()) {
    unseen[notice.failed ? "failed" : "ready"] += 1;
    showNotification(notice);
  }
  render();
}

// sticky: stay on screen until clicked instead of sliding away after a few
// seconds. They're closed for you once you're back on the tab (onReturn).
function showNotification({ title, body, onClick, sticky = true }) {
  if (!notificationsEnabled()) return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/favicon.ico",
      tag: "alice-reply",
      renotify: true,
      requireInteraction: sticky,
    });
    openNotifications.add(n);
    n.onclose = () => openNotifications.delete(n);
    n.onclick = () => {
      window.focus();
      if (onClick) onClick();
      n.close();
    };
  } catch (e) {
    // Some browsers only allow notifications from a service worker.
    console.warn("reply notification failed:", e);
  }
}

function onReturn() {
  if (!userIsLooking() || !(unseen.ready || unseen.failed)) return;
  unseen.ready = 0;
  unseen.failed = 0;
  openNotifications.forEach((n) => n.close());
  render();
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", onReturn);
  window.addEventListener("focus", onReturn);
}

// ----- Title + favicon -----

// Unseen results win over a still-running reply: the point is to pull the
// user back, and they'll see the rest once they're there.
export function tabState({ pending: busy, ready, failed }) {
  if (ready) return "ready";
  if (failed) return "failed";
  return busy ? "busy" : "idle";
}

export function decoratedTitle(state, base, ready) {
  if (state === "ready") {
    return ready > 1
      ? `✅ ${ready} answers ready · ${base}`
      : `✅ Answer ready · ${base}`;
  }
  if (state === "failed") return `⚠️ Alice couldn't answer · ${base}`;
  if (state === "busy") return `⏳ Thinking… · ${base}`;
  return base;
}

function render() {
  const state = tabState({ pending: pending.size, ...unseen });
  // Capture the undecorated title/icon on the way out of idle, so whatever
  // the app shows at that moment is what we restore.
  if (renderedState === "idle" && state !== "idle") {
    baseTitle = document.title;
    baseIcon = iconLink().getAttribute("href");
  }
  if (state === "idle" && renderedState === "idle") return;
  document.title = decoratedTitle(state, baseTitle, unseen.ready);
  renderedState = state;
  if (state === "idle") setIcon(baseIcon);
  else drawBadgedIcon(state);
}

function iconLink() {
  let link = document.querySelector('link[rel~="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  return link;
}

function setIcon(href) {
  if (href) iconLink().setAttribute("href", href);
}

function drawBadgedIcon(state) {
  const paint = () => {
    // A late image load must not repaint over a state that has moved on.
    if (renderedState !== state) return;
    const size = 32;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (iconImage && iconImage.complete && iconImage.naturalWidth) {
      ctx.drawImage(iconImage, 0, 0, size, size);
    }
    ctx.beginPath();
    ctx.arc(size - 10, 10, 9, 0, Math.PI * 2);
    ctx.fillStyle = BADGE_COLORS[state];
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
    setIcon(canvas.toDataURL("image/png"));
  };
  if (!iconImage) {
    iconImage = new Image();
    iconImage.src = baseIcon || "/favicon.ico";
  }
  if (iconImage.complete) return paint();
  // On error we still paint: a bare dot beats no signal.
  iconImage.addEventListener("load", paint, { once: true });
  iconImage.addEventListener("error", paint, { once: true });
}
